import React from 'react';
import { AutoTable, EmptyState, Icon, Card, Button, Dropdown, Status, Loader } from '@weezevent/nacre';
import { useBankAccountsStore } from '../store';
import { EditBankAccountDrawerProvider, useEditBankAccountDrawer } from '../editDrawer';
import { FormattedDateTime } from '../../../../../../components/FormattedDateTime';
import { AccountNumber } from './AccountNumber';
import { StopPropagation } from '../../../../../../components/StopPropagation';
import { useTranslation } from 'react-i18next';
import { DeleteBankAccountModalProvider, useDeleteBankAccountModal } from '../deleteEntityModal';
import { LoadingStatus, useStore } from '../../../store';

export const BankAccountsContent = () => {
    const { t } = useTranslation();
    const store = useStore();
    const { bankAccounts, status } = useBankAccountsStore();
    const drawer = useEditBankAccountDrawer();
    const deleteModal = useDeleteBankAccountModal();

    const columns = [
        {
            key: 'name',
            label: t('sauron.financeModule.bankAccounts.table.columns.name')
        },
        {
            key: 'created_at',
            label: t('sauron.financeModule.bankAccounts.table.columns.createdAt'),
            render: ({ created_at }) => <FormattedDateTime date={new Date(created_at)} />
        },
        {
            key: 'account_number',
            label: t('sauron.financeModule.bankAccounts.table.columns.accountNumber'),
            render: data => <AccountNumber data={data} />
        },
        {
            key: 'is_confirmed',
            label: t('sauron.financeModule.bankAccounts.table.columns.verified'),
            render: ({ is_confirmed }) => {
                return (
                    <Status color={is_confirmed ? 'green' : 'orange'} responsive>
                        {is_confirmed ? t('sauron.financeModule.bankAccounts.table.status.valid') : t('sauron.financeModule.bankAccounts.table.status.pending')}
                    </Status>
                );
            }
        },
        {
            key: 'actions',
            label: '',
            render: data => {
                return (
                    <StopPropagation>
                        <Dropdown>
                            <Dropdown.Item onClick={() => drawer.edit(data)}>{t('common.cta.edit')}</Dropdown.Item>
                            <Dropdown.Item disabled={!store.isBankAccountDeletable(data.id)} onClick={() => deleteModal.open(data.id)}>
                                {t('common.cta.delete')}
                            </Dropdown.Item>
                        </Dropdown>
                    </StopPropagation>
                );
            }
        }
    ];

    const renderContent = () => {
        if (status === LoadingStatus.LOADING) {
            return <Loader active inline="centered" />;
        }

        return (
            <>
                <AutoTable
                    useNew
                    columns={columns}
                    objects={bankAccounts}
                    emptyState={
                        <EmptyState
                            title={t('sauron.financeModule.bankAccounts.empty.title')}
                            icon={<Icon margin name="list" size="regular" />}
                            labelButton={t('sauron.financeModule.bankAccounts.cta.add')}
                            onClick={drawer.create}
                        />
                    }
                />
                {status !== LoadingStatus.EMPTY && (
                    <Button primary as="button" onClick={drawer.create}>
                        {t('sauron.financeModule.bankAccounts.cta.add')}
                    </Button>
                )}
            </>
        );
    };

    return (
        <Card>
            <Card.Header>
                <Card.Title>{t('sauron.financeModule.bankAccounts.title')}</Card.Title>
            </Card.Header>
            <Card.Content>{renderContent()}</Card.Content>
        </Card>
    );
};

export const BankAccounts = () => {
    return (
        <EditBankAccountDrawerProvider>
            <DeleteBankAccountModalProvider>
                <BankAccountsContent />
            </DeleteBankAccountModalProvider>
        </EditBankAccountDrawerProvider>
    );
};
